import type { OperatingFlow, PlainCopy } from './plainLanguage';

/** English operator/buyer copy. Structure must match Korean PLAIN. */
export const PLAIN_EN: PlainCopy = {
  brand: {
    definition:
      'A space-operations AI platform that turns CCTV, sensors, and site equipment into situation assessment, operator confirmation, field action, and evidence.',
    oneLiner: 'Turn existing CCTV into a system that judges and acts.',
    heroLead:
      'We prioritize scenes that need a human look, then carry confirmation, action, and a record. Cameras stay in place.',
    trustNote: 'Keep video on site. Send short alerts and metadata upward.',
    positioning:
      'Unlike recording-first VMS or generic “person/car detected” AI, we select scenes that need confirmation and connect them to response.',
    differentiator:
      'We do not replace existing CCTV or VMS. Alert wording and connectors are already shaped for parking, buildings, security, and plants.',
    label: 'PARKWAVE · Space operations AI',
    short: 'See the space, judge, then move the operation',
  },
  terms: {
    event: 'Event',
    eventHint: 'A single signal from a camera or device.',
    situation: 'Situation',
    situationHint: 'Related events grouped so an operator can confirm and act.',
    alert: 'Alert',
    alertHint: 'A message that tells a person that an event or situation changed.',
    confidence: 'Detection confidence',
    confidenceHint: 'How sure the model is of that class. It is not a real-world risk probability.',
    severity: 'Severity',
    severityHint: 'Handling priority from policy, time, and location — not a legal finding.',
    zone: 'Analysis zone',
    zoneHint: 'The region inside the frame the model watches more closely.',
  },
  audience: {
    operator: 'Operations / SOC',
    buyer: 'Procurement / owner',
    it: 'IT / SI',
  },
  try: {
    title: 'Try analysis on a sample clip',
    lead: 'Upload a photo or short video without signing in. You can also switch the alert wording for your site type.',
    steps: [
      { title: 'Pick a site type', desc: 'Choose parking, building, plant, or security.' },
      { title: 'Upload a file', desc: 'Drop or select a photo or short video.' },
      { title: 'See results and sample alerts', desc: 'See detections and the alerts an operator would actually receive.' },
    ],
    productLineHint: 'Changing the button only changes alert wording. The analysis path is the same.',
    notProduction:
      'The public trial is an example to explain the product. Do not treat it as field accuracy or latency.',
    capabilitiesOk: 'Trial analysis is ready. You can upload now.',
    capabilitiesDemo: 'This may be a simple demo mode. Upload and alert format still work.',
    capabilitiesFail:
      'Cannot reach the analysis server. Try again later or ask IT to check the API connection.',
    capabilitiesTechToggle: 'Technical detail (IT)',
  },
  login: {
    title: 'PARKWAVE operations console',
    lead: 'Sign in with a registered account.',
    demoLead: 'Use the demo account to look at operations and multi-source events first.',
    demoNote: 'Change the password before any real use.',
    failHint: 'If sign-in fails, ask IT whether the operations API is up.',
    apiOnline: 'Connected to the operations API',
    apiOffline: 'Cannot reach the operations API. Check the URL and deploy status.',
    beforeLogin: 'New here? Start with the sample-clip trial to see alert format.',
    submit: 'Sign in',
  },
  console: {
    dashboard: 'Operations',
    dashboardDesc: 'Events, review queue, and device/API status in one place.',
    fusion: 'All sources',
    fusionDesc: 'Signals from cameras, radar, and site gear in one list you can inspect.',
    training: 'AI training',
    startGuide: 'Get started',
    welcome:
      'See events that need a look today, plus review feedback. Use ↑↓ in the table and Enter to confirm.',
    fusionWelcome: 'Events from many sources in one line. Use Demo add or Site sim for samples.',
    eventsAutoRefresh: 'Updates every 3 seconds',
    eventsEmpty: 'No events yet. Open All sources and try Demo add or Site sim.',
    onboardingBanner: 'New here? Get started walks you from trial → operations → integration checklist.',
    feedbackTitle: 'Detection review',
    eventsTitle: 'Recent events',
    eventsHelp: 'Events are individual signals. Related signals can be grouped into a situation.',
    sampleEvent: 'Insert sample event',
    demoIngest: 'Demo add',
    edgeSim: 'Site sim',
    refresh: 'Refresh',
    headerProduct: 'Operations console',
    logout: 'Sign out',
    loginLink: 'Sign in',
    skipToContent: 'Skip to main content',
    userFallback: 'User',
    userHonorific: '',
    menuAria: 'App menu',
    mainAria: 'App content',
    timelineEmpty: 'No events to show. Widen the time or severity filter, or insert a sample event.',
    secondLook: 'Second look',
    episode: 'Episode',
  },
  onboarding: {
    title: 'Get started',
    lead: 'Confirm site type and alert format, then continue into the operations screens.',
    dismiss: 'Don’t show again',
    openGuide: 'Open guide',
  },
  integration: {
    title: 'Connect systems you already use',
    lead: 'VMS, SOC, tickets, and sensors attach to the same short-alert shape. Match field names before writing code.',
    ctaDocs: 'See how to connect',
  },
  marketing: {
    ctaTry: 'Try analysis on a sample clip',
    ctaDocs: 'See how the product works',
    ctaLogin: 'Product sign-in',
    ctaAppHub: 'Check fit for our site',
    ctaPoc: 'Confirm PoC scope',
    navParking: 'Parking',
    navBuilding: 'Building',
    navSecurity: 'Security',
    navFactory: 'Plant',
    navReports: 'Industry reports',
    navExplore: 'Explore the product',
    externalPortal: 'External portal',
    sitePark: 'Parking & buildings',
    siteParkHint: 'Occupancy, dwell, and congestion as ops metrics',
    siteParkDetail:
      'Occupancy, vehicle motion, dwell, and congestion become alerts and metrics operators can use.',
    siteAm: 'Facilities',
    siteAmHint: 'Lobby and perimeter in the same alert shape',
    siteAmDetail:
      'Tie access and building ops into one line. Only scenes that need a look reach operators and security.',
    siteArms: 'Public safety & security',
    siteArmsHint: 'Perimeter and intrusion as situations',
    siteArmsDetail: 'Keep the current VMS. Short, evidence-backed alerts continue confirmation and response.',
    siteInfra: 'Plant & logistics',
    siteInfraHint: 'Dwell and paths for site operations',
    siteInfraDetail: 'Zone dwell and unusual paths become ops alerts, next to existing plant systems.',
    valueTitle: 'What does it do?',
    valueBody:
      'It selects CCTV scenes that need a look and turns them into events and situations an operator can read. Cameras stay.',
    problemTitle: 'Cameras grew. Judgment and response are still stitched by people.',
    problemBody:
      'Classic VMS stores and plays video. When something looks wrong, a person watches several screens, ranks risk, calls someone, and writes the report. Parkwave connects what happens after detection into one operating flow.',
    flowTitle: 'One operating flow from detect to judgment, confirmation, and evidence',
    vsTitle: 'How is this different?',
    securityTitle: 'Deploy to match how sensitive video and ops data are',
    securityLead:
      'Choose cloud, edge, or on-prem by site. Prefer video on site and alerts upward.',
    trustStrip: 'Keep CCTV & VMS · cloud, edge, on-prem · start with a limited site',
  },
  honesty: {
    kicker: 'Current state, without hype',
    techTitle: 'Technical',
    techBody:
      'The edge pipeline (detect, ROI dwell, HMAC, uplink) is in code. The public cloud trial is a demo — do not treat it as on-site GPU/NPU performance.',
    useTitle: 'Real use',
    useBody:
      'A one-site pilot is coded. The remaining gate is attaching live cameras and rotating secrets. Multi-site SaaS and OAuth are not here yet.',
    salesTitle: 'Sales',
    salesBody:
      'The real wedge is keep-the-cameras plus situation-level ops. Do not sell it as Genetec/Verkada-complete VMS or camera-replacement cloud.',
  },
  diagram: {
    conceptAria: 'How events, situations, and alerts relate',
    join: 'Group',
    deliver: 'Notify',
    eventEx1: 'Person detected',
    eventEx2: 'Zone entry',
    eventEx3: 'Link lost',
    sitEx1: 'North fence intrusion suspected',
    sitEx2: 'Operator confirm and act',
    alertEx1: 'App, SMS, email',
    alertEx2: 'Webhook / ticket',
    mockAria: 'Sample operations view',
    mockLabel: 'Sample operations view',
    mockTitle: 'Motion at north fence',
    mockBody: 'The same object was seen on two cameras in sequence.',
    mockUrgent: 'Act now',
    mockAgo: '42s ago',
    mockRec: 'Suggested check',
    mockRecBody: 'Check the perimeter with thermal or a neighboring camera.',
    mockEvidence: 'See evidence',
    mockProcess: 'See response path',
    flowDetect: 'Detect',
    flowJudge: 'Judge',
    flowConfirm: 'Confirm',
    flowAct: 'Act',
    flowRecord: 'Record',
    compareAria: 'Classic approach vs Parkwave',
    compareBefore: 'Classic',
    compareAfter: 'PARKWAVE',
    before1: 'People watch video walls',
    before2: 'Alerts repeat as fragments',
    before3: 'Approval via phone and chat',
    before4: 'Reports by hand',
    after1: 'Only situations that need a look',
    after2: 'Related events grouped',
    after3: 'Act on-screen after evidence',
    after4: 'Video, judgment, and action logged',
    deployAria: 'Deployment options',
    deployEdge: 'On-site edge',
    deployEdgeD: 'Analyze video on site',
    deployCloud: 'Cloud',
    deployCloudD: 'Summaries, reports, connectors',
    deployOnprem: 'On-prem',
    deployOnpremD: 'Run inside the customer network',
    deployAirgap: 'Air-gap',
    deployAirgapD: 'Fit isolation requirements',
  },
  chrome: {
    wordmark: 'Parkwave',
    wordmarkLatin: 'Parkwave',
    titleSuffix: 'Parkwave',
    documentTitle: 'Parkwave | Turn existing CCTV into an operations system',
    metaDescription:
      'Parkwave turns existing CCTV and site signals into detection, priority, confirmation, action, and evidence — without replacing cameras.',
    homeAria: 'Parkwave home',
    langAria: 'Language',
    langKo: '한국어',
    langEn: 'English',
  },
  health: {
    apiOffline: 'API offline',
    apiOfflineAria: 'API connection failed',
    stream: 'Stream',
    demo: '(demo)',
  },
  buttons: {
    confirmSituation: 'Confirm situation',
    save: 'Save',
    closeWithoutChange: 'Close without changes',
    retryVideo: 'Reload video',
    notifyAssignee: 'Notify assignee',
  },
  empty: {
    events: 'No events match. Widen the time range or severity.',
    generic: 'Nothing to show.',
  },
};

export const OPERATING_FLOW_EN: OperatingFlow = [
  { title: 'Collect site signals', desc: 'Connect existing CCTV, sensors, and site equipment.' },
  { title: 'Detect events', desc: 'Turn people, vehicles, zone entry, and faults into ops events.' },
  { title: 'Judge the situation', desc: 'Group related events and rank severity and handling order.' },
  { title: 'Guide response', desc: 'Suggest the next action from policy and site state.' },
  { title: 'Confirm and act', desc: 'The operator checks evidence and carries out action or handoff.' },
  { title: 'Evidence and improve', desc: 'Keep video, judgment, and action, then feed operations later.' },
];
